import { alphabeticalCompare } from '@cims-online/shared'
import type { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import RecipientsTags from 'components/CIMSTags/common/RecipientsTags'
import type {
  Notification,
  NotificationField,
  NotificationStatus,
} from '../types'
import { summarizeRecipients, summarizeWhenAndIf } from '../utils/summary'

type UseColumnsOptions = {
  fields: NotificationField[]
  statuses: NotificationStatus[]
}

/**
 * Builds the Notifications table columns, using workflow metadata for condition
 * labels and the current project user context for recipient tags.
 */
export function useColumns({
  fields,
  statuses,
}: UseColumnsOptions): CIMSColumn<Notification>[] {
  return [
    {
      title: 'Sent When',
      help: 'The trigger and conditions required to send this notification.',
      render: (notification) =>
        summarizeWhenAndIf(notification, { fields, statuses }),
      sort: {
        with: 'callback',
        onCompare: (a, b) =>
          alphabeticalCompare(
            summarizeWhenAndIf(a, { fields, statuses }),
            summarizeWhenAndIf(b, { fields, statuses })
          ),
      },
      width: 300,
    },
    {
      title: 'Recipients',
      help: 'Users and non-user email addresses that receive this notification.',
      render: ({ to }) => (
        <RecipientsTags
          noRecipientsContent="No recipients"
          recipients={{
            users: to.filter((recipient) => recipient.__typename === 'User'),
            nonUserEmails: to.filter(
              (recipient) => recipient.__typename === 'NonUserEmail'
            ),
          }}
        />
      ),
      sort: {
        with: 'callback',
        onCompare: (a, b) =>
          alphabeticalCompare(summarizeRecipients(a), summarizeRecipients(b)),
      },
      filter: {
        type: 'search',
        with: 'callback',
        onFilter: (value, { to }) => {
          const search = value.toString().toLowerCase()

          if (!to.length) return 'no recipients'.includes(search)

          return to.some((recipient) => {
            const searchableValues =
              recipient.__typename === 'User'
                ? [recipient.displayName, recipient.mail]
                : [recipient.label, recipient.address]

            return searchableValues.some((searchableValue) =>
              searchableValue?.toLowerCase().includes(search)
            )
          })
        },
      },
      width: 300,
    },
    {
      title: 'Template',
      help: 'The template used to generate the notification message.',
      render: ({ notificationTemplate }) => notificationTemplate.name,
      sort: {
        with: 'callback',
        onCompare: (a, b) =>
          alphabeticalCompare(
            a.notificationTemplate.name,
            b.notificationTemplate.name
          ),
      },
      filter: {
        type: 'search',
        with: 'callback',
        onFilter: (value, { notificationTemplate }) =>
          notificationTemplate.name
            .toLowerCase()
            .includes(value.toString().toLowerCase()),
      },
      width: 200,
    },
  ]
}
